import { createFileRoute, Link } from "@tanstack/react-router";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy & Cookies — Hantavirus International" },
      { name: "description", content: "Privacy policy and cookie usage for Hantavirus International outbreak tracker." },
      { name: "keywords", content: "hantavirus privacy policy, hantavirus international, hantavirus tracker privacy, hantavirus cookies, hantavirus data policy" },
      { name: "robots", content: "index, follow" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <HVIHeader />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6 text-sm leading-relaxed">
          <header className="border-b border-border pb-4">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Privacy & Cookies</h1>
            <p className="text-muted-foreground mt-2">Last updated: May 2026</p>
          </header>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">What we collect</h2>
            <p>
              Hantavirus International is a public outbreak dashboard. We do not require you to create an account.
              We collect the minimum data needed to keep the site fast, secure, and free.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Anonymous usage analytics (page views, country, device class) — only if you accept cookies.</li>
              <li>Server logs (IP, user agent, request path) retained for security and abuse prevention.</li>
              <li>No health data, no personal identifiers, no contact information is collected from visitors.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">Cookies</h2>
            <p>
              We use a consent banner. If you click <strong>Accept</strong>, we enable Google AdSense
              and anonymous analytics cookies. If you click <strong>Reject</strong>, only strictly necessary
              cookies are used (session, language preference). You can change your choice at any time by clearing
              site data in your browser.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">Advertising</h2>
            <p>
              When ads are enabled, Google and its partners may use cookies to serve ads based on your visits to
              this and other sites. You can opt out of personalised advertising at{" "}
              <a className="text-hvi-primary hover:underline" href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer">
                Google Ad Settings
              </a>{" "}
              or{" "}
              <a className="text-hvi-primary hover:underline" href="https://www.aboutads.info" target="_blank" rel="noreferrer">
                aboutads.info
              </a>.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">Data sources</h2>
            <p>
              All case data is republished from public bulletins (WHO, CDC, ProMED-mail, national ministries).
              We do not collect or store information about individual patients.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">Privacy Policy</h2>
            <p>
              Hantavirus International respects your privacy. This site is operated as a public health information
              resource and does not require registration. We do not sell, trade, or share any data with third
              parties for marketing purposes.
            </p>
            <p>
              Any information voluntarily submitted through the feedback form (name, email, message) is used
              solely to respond to your inquiry. Feedback data is stored securely and is not publicly accessible.
            </p>
            <p>
              We reserve the right to update this policy as needed. Changes will be reflected on this page with an
              updated revision date.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold uppercase tracking-wider text-hvi-primary">Contact</h2>
            <p>
              For privacy-related questions, data removal requests, or any other inquiries, please contact us at{" "}
              <a href="mailto:info@hantavirus.international" className="text-hvi-primary hover:underline">
                info@hantavirus.international
              </a>.
            </p>
          </section>
        </div>
      </main>
      <HVIFooter />
    </div>
  );
}
